import { AdminApiError, type AdminClient, type ApiKeyView } from '@apx/admin-sdk';
import { emptyState } from '../empty-state.js';
import { askConfirm } from '../modal.js';
import { fmtDate, fmtDateTime } from '../format.js';
import { showToast } from '../toast.js';

export interface ApiKeysPageDeps {
  client: AdminClient;
  integratorId: string;
}

export async function renderApiKeysPage(root: HTMLElement, deps: ApiKeysPageDeps): Promise<void> {
  root.innerHTML = `
    <h1 class="page-title">API keys</h1>
    <p class="page-sub">Integrator-facing keys for the public <code>POST /api/v1/sessions</code> endpoint. The plaintext key is shown <strong>once</strong> at creation; only the SHA-256 hash is persisted. Revoke = soft delete (audit trail preserved).</p>

    <div class="card">
      <h2>Issue new key</h2>
      <div class="row">
        <button id="ak-create" class="primary">Generate</button>
        <span id="ak-msg" class="muted"></span>
      </div>
      <div id="ak-reveal"></div>
    </div>

    <div class="card">
      <h2>Existing keys</h2>
      <div id="ak-list"><p class="muted">Loading…</p></div>
    </div>
  `;

  const msgEl = root.querySelector<HTMLElement>('#ak-msg')!;
  const revealEl = root.querySelector<HTMLElement>('#ak-reveal')!;
  const listEl = root.querySelector<HTMLElement>('#ak-list')!;

  async function refresh(): Promise<void> {
    try {
      const { keys } = await deps.client.listApiKeys(deps.integratorId);
      listEl.innerHTML = renderTable(keys);
      bindRevoke();
    } catch (err) {
      listEl.innerHTML = `<p class="error">${escapeHtml(describe(err))}</p>`;
    }
  }

  function renderTable(keys: ApiKeyView[]): string {
    if (keys.length === 0) {
      return `
        ${emptyState({
          icon: 'plug',
          title: 'No API keys issued yet',
          hint:
            'Without a key this integrator cannot authenticate calls to POST /api/v1/sessions, so no player sessions can be minted and the iframe cannot launch.',
        })}
        <div class="onboarding-checklist">
          <div class="onboarding-checklist__title">Next step</div>
          <ol class="onboarding-checklist__steps">
            <li>Click <strong>Generate</strong> above. Plaintext key shown <em>once</em> — copy immediately.</li>
            <li>Send the plaintext to the integrator via a secure channel (1Password, signed email, in-person).</li>
            <li>Integrator stores it as a server-side env var (never in client code).</li>
            <li>Integrator calls <code>POST /api/v1/sessions</code> with header <code>Authorization: ApiKey &lt;plaintext&gt;</code>.</li>
            <li>apx returns a short-lived session JWT — integrator passes it to the iframe via <code>postMessage</code>.</li>
          </ol>
        </div>
      `;
    }
    return `<table>
      <thead><tr><th>ID</th><th>Status</th><th>Created</th><th>Last used</th><th>Rate limit</th><th></th></tr></thead>
      <tbody>${keys
        .map(
          (k) => `<tr>
            <td><code>${k.id.slice(0, 8)}…</code></td>
            <td><span class="pill ${k.status === 'active' ? 'live' : 'warn'}">${k.status}</span></td>
            <td>${fmtDate(k.createdAt)}</td>
            <td>${k.lastUsedAt ? fmtDateTime(k.lastUsedAt) : '—'}</td>
            <td>${k.rateLimit}/min</td>
            <td>${k.status === 'active' ? `<button data-revoke="${k.id}" class="ghost">Revoke</button>` : ''}</td>
          </tr>`,
        )
        .join('')}</tbody>
    </table>`;
  }

  function bindCopy(): void {
    const btn = revealEl.querySelector<HTMLButtonElement>('#ak-copy');
    if (!btn) return;
    btn.addEventListener('click', async () => {
      const secret = btn.dataset.secret ?? '';
      const original = btn.textContent;
      try {
        if (navigator.clipboard?.writeText) {
          await navigator.clipboard.writeText(secret);
        } else {
          const secretEl = revealEl.querySelector<HTMLElement>('#ak-secret');
          if (secretEl) {
            const range = document.createRange();
            range.selectNodeContents(secretEl);
            const sel = window.getSelection();
            sel?.removeAllRanges();
            sel?.addRange(range);
            document.execCommand('copy');
            sel?.removeAllRanges();
          }
        }
        btn.textContent = 'Copied';
        btn.disabled = true;
        setTimeout(() => {
          btn.textContent = original;
          btn.disabled = false;
        }, 1500);
      } catch (err) {
        showToast(describe(err), 'error');
      }
    });
  }

  function bindRevoke(): void {
    listEl.querySelectorAll<HTMLButtonElement>('button[data-revoke]').forEach((btn) => {
      btn.addEventListener('click', async () => {
        if (!(await askConfirm('Revoke this API key? Integrators using it will fail HMAC verification.'))) return;
        try {
          await deps.client.revokeApiKey(btn.dataset.revoke!);
          await refresh();
        } catch (err) {
          showToast(describe(err), 'error');
        }
      });
    });
  }

  root.querySelector<HTMLButtonElement>('#ak-create')!.addEventListener('click', async () => {
    msgEl.textContent = '';
    revealEl.innerHTML = '';
    try {
      const created = await deps.client.createApiKey(deps.integratorId);
      revealEl.innerHTML = `
        <div class="card" style="margin-top:14px;background:var(--panel-2);border-color:var(--accent)">
          <h3 style="margin:0 0 10px;color:var(--accent)">Save this key now — it will not be shown again</h3>
          <div class="row" style="align-items:stretch;gap:8px">
            <div id="ak-secret" class="diff" style="flex:1;user-select:all;font-size:14px;color:var(--text);background:var(--bg);word-break:break-all">${escapeHtml(created.plaintextKey)}</div>
            <button id="ak-copy" class="primary" type="button" data-secret="${escapeHtml(created.plaintextKey)}">Copy</button>
          </div>
          <p class="muted" style="margin-top:8px">Key id: <code>${created.id}</code> · Created ${fmtDateTime(created.createdAt)}</p>
        </div>
      `;
      bindCopy();
      msgEl.textContent = 'Generated — copy the secret above.';
      msgEl.className = 'muted';
      await refresh();
    } catch (err) {
      msgEl.textContent = describe(err);
      msgEl.className = 'error';
    }
  });

  await refresh();
}

function describe(err: unknown): string {
  if (err instanceof AdminApiError) return `${err.code}: ${err.message}`;
  return err instanceof Error ? err.message : String(err);
}

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) => {
    switch (c) {
      case '&':
        return '&amp;';
      case '<':
        return '&lt;';
      case '>':
        return '&gt;';
      case '"':
        return '&quot;';
      case "'":
        return '&#39;';
      default:
        return c;
    }
  });
}
